import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Alert, Box, Snackbar } from '@mui/material';
import { useSelector } from 'react-redux';

import AppListToolbarGlobal from '@/modules/inventory/stockbalance/components/StockBalancesToolbarGlobal';
import { resolveActiveCompanyFilter, resolveToolbarTabCompanyFilter } from '@/__core/hooks/useCompanyContext';
import { useModuleTabsWithGuard } from '@/__core/hooks/useModuleTabsWithGuard';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import MainListModule from '@/__core/layout/module/MainListModule';
import { useModuleToolbarPermissions, isTabVisibleForToolbarCompany } from '@/__core/permissions';
import { TabData } from '@/__core/types/TabData';
import ModuleTabsHeader from '@/modules/users/components/ModuleTabsHeader';
import { RootState } from '@/setup/redux/RootReducer';

import {
  createDefaultStockBalanceForm,
  InventoryStockBalanceFormData,
  InventoryStockBalanceRow,
  stockBalanceStateOptions,
} from '../types';
import StockBalanceForm from './StockBalanceForm';
import StockBalanceList, { StockBalanceListHandle } from './StockBalanceList';

const buildFormMetadata = (formData: InventoryStockBalanceFormData, stockBalanceId?: string) => ({
  formData,
  stockBalanceId,
});

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const resolveCompanyIdForQuery = (companyId?: string) => {
  if (!companyId || companyId === 'all' || companyId === 'global' || !UUID_PATTERN.test(companyId)) {
    return '';
  }
  return companyId;
};

export default function StockBalanceModule() {
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);
  const listRef = useRef<StockBalanceListHandle>(null);

  const [snackbarMessage, setSnackbarMessage] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [filters, setFilters] = useState({ company: 'all', stockState: 'all' });
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const selectedCompanyId = useMemo(
    () => String(selectedCompany?.company_id || getCurrentCompanyId() || '').trim(),
    [selectedCompany?.company_id],
  );

  const activeCompanyFilter = useMemo(
    () => resolveActiveCompanyFilter(selectedCompanyId, filters.company),
    [selectedCompanyId, filters.company],
  );

  const toolbarTabCompanyFilter = useMemo(
    () => resolveToolbarTabCompanyFilter(filters.company),
    [filters.company],
  );

  const listScopeCompanyId = useMemo(
    () => resolveCompanyIdForQuery(activeCompanyFilter),
    [activeCompanyFilter],
  );

  const {
    canCreate: canCreateStockBalance,
    canUpdate: canUpdateStockBalance,
    resolveTabPermissions,
    resolvePermissionsForCompanyId,
    canReadRecord,
  } = useModuleToolbarPermissions({
    resource: 'inventory.stockbalance',
    activeCompanyFilter,
    updateTabTypes: ['update-stock-balance'],
  });

  const defaultTabs: TabData[] = useMemo(() => {
    return [
      { id: 0, title: 'Số dư tồn kho', type: 'list', closable: false, createdAt: Date.now() },
    ];
  }, []);

  const {
    storedTabs,
    activeTab,
    updateTab,
    tabs,
    visibleActiveTab,
    handleVisibleTabChange,
    isStoredTabPanelActive,
    handleCloseTab,
    handleAddTab,
    handleUnsavedChangesUpdate,
    guardAndRun,
    openTab,
    unsavedDialog,
  } = useModuleTabsWithGuard({
    storageKey: 'inventory_stock_balance_tabs',
    defaultTabs,
    maxTabs: 12,
    canCreate: Boolean(canCreateStockBalance),
    toolbarCompanyId: toolbarTabCompanyFilter,
    guardedTabTypes: ['new-stock-balance', 'update-stock-balance'],
    allowMultipleTabTypes: ['new-stock-balance', 'update-stock-balance'],
    buildNewTab: (currentTabs) => {
      const baseCompanyId = listScopeCompanyId || selectedCompanyId;
      return {
        id: Date.now() + Math.floor(Math.random() * 1000),
        title: `Tồn kho #${currentTabs.filter((tab) => tab.type === 'new-stock-balance').length + 1}`,
        type: 'new-stock-balance',
        closable: true,
        metadata: {
          companyId: baseCompanyId,
          ...buildFormMetadata(createDefaultStockBalanceForm(baseCompanyId)),
        },
      };
    },
  });

  const activeTabData = storedTabs[activeTab];
  const activeFormData = activeTabData?.metadata?.formData as InventoryStockBalanceFormData | undefined;
  const activeStockBalanceId = activeTabData?.metadata?.stockBalanceId as string | undefined;

  const handleToolbarFilterChange = (field: string, value: string) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const updateFormData = (updates: Partial<InventoryStockBalanceFormData>) => {
    if (!activeTabData?.id || !activeFormData) return;

    const isUpdateMode = Boolean(activeStockBalanceId);
    const tabPermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    const canEditCurrentTab = isUpdateMode ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) {
      return;
    }

    const nextFormData = { ...activeFormData, ...updates };
    updateTab(activeTabData.id, { metadata: buildFormMetadata(nextFormData, activeStockBalanceId) });
    handleUnsavedChangesUpdate(true, activeTabData.id);
  };

  const openExistingStockBalance = (row: InventoryStockBalanceRow) => {
    if (!canReadRecord(row)) {
      setSnackbarMessage({ message: 'Bạn không có quyền xem chi tiết tồn kho.', severity: 'error' });
      return;
    }
    guardAndRun(() => {
      openTab({
        id: Date.now() + Math.floor(Math.random() * 1000),
        title: row.item_code,
        type: 'update-stock-balance',
        closable: true,
        entityId: row.id,
        metadata: {
          companyId: row.company_id,
          ...buildFormMetadata(
          {
            item_code: row.item_code,
            item_name: row.item_name,
            store_code: row.store_code,
            store_name: row.store_name,
            lot_no: row.lot_no,
            bin_location: row.bin_location,
            on_hand_qty: String(row.on_hand_qty),
            allocated_qty: String(row.allocated_qty),
            available_qty: String(row.available_qty),
            last_price: String(row.last_price),
            updated_at: row.updated_at,
            company_id: row.company_id,
            company_name: row.company_name || '',
            last_received_at: row.last_received_at || '',
            last_movement_at: row.last_movement_at || '',
            note: row.note || '',
          },
          row.id
        ),
        },
      });
    });
  };

  const handleSave = () => {
    if (!activeFormData || !activeTabData?.id) return;

    const isUpdateMode = Boolean(activeStockBalanceId);
    const savePermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    if (isUpdateMode && !savePermissions.canUpdate) {
      setSnackbarMessage({ message: 'Bạn không có quyền cập nhật bản ghi tồn kho.', severity: 'error' });
      return;
    }

    if (!isUpdateMode && !savePermissions.canCreate) {
      setSnackbarMessage({ message: 'Bạn không có quyền tạo bản ghi tồn kho.', severity: 'error' });
      return;
    }

    handleUnsavedChangesUpdate(false, activeTabData.id);
    setSnackbarMessage({
      message: 'Số dư tồn kho được tính tự động từ chứng từ kho đã ghi sổ, không chỉnh sửa trực tiếp.',
      severity: 'error',
    });
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value);
    if (value === '') {
      setSearchQuery('');
    }
  };

  const handleSearchSubmit = () => {
    setSearchQuery(searchInputValue.trim());
  };

  const handleRefresh = useCallback(() => {
    listRef.current?.requestRefresh({ showSuccessMessage: true });
  }, []);

  const handleRefreshSuccess = useCallback(() => {
    setSnackbarMessage({ message: 'Đã refresh số dư tồn kho.', severity: 'success' });
  }, []);

  return (
    <>
      <ModuleTabsHeader
        tabs={tabs}
        activeTab={visibleActiveTab}
        currentTabType={activeTabData?.type}
        onTabChange={handleVisibleTabChange}
        onCloseTab={handleCloseTab}
        onAddTab={handleAddTab}
        moduleType='inventory'
        unsavedDialog={unsavedDialog}
      />

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            {storedTabs.map((tab, index) => {
              if (!isTabVisibleForToolbarCompany(tab, toolbarTabCompanyFilter)) {
                return null;
              }
              const isActive = isStoredTabPanelActive(tab);
              const tabFormData = tab.metadata?.formData as InventoryStockBalanceFormData | undefined;
              const tabPermissions = resolveTabPermissions(tab);

              return (
                <Box key={tab.id} sx={{ display: isActive ? 'flex' : 'none', width: '100%', height: '100%' }}>
                  {tab.type === 'list' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                      <MainListModule
                        leftSidebar={null}
                        toolbar={
                          <AppListToolbarGlobal
                            searchQuery={searchInputValue}
                            onSearchChange={handleSearchInputChange}
                            onSearchSubmit={handleSearchSubmit}
                            onToggleAdvancedSearch={() => setIsAdvancedSearchOpen((current) => !current)}
                            isAdvancedSearchOpen={isAdvancedSearchOpen}
                            filters={{
                              company: filters.company || 'all',
                              stockState: filters.stockState,
                            }}
                            onFilterChange={handleToolbarFilterChange}
                            stockStateOptions={stockBalanceStateOptions}
                            onRefresh={handleRefresh}
                          />
                        }
                        dataTable={
                          <StockBalanceList
                            ref={listRef}
                            companyId={listScopeCompanyId || selectedCompanyId}
                            searchQuery={searchQuery}
                            stockState={filters.stockState}
                            onSelectRow={openExistingStockBalance}
                            onFetchError={(message) => setSnackbarMessage({ message, severity: 'error' })}
                            onRefreshSuccess={handleRefreshSuccess}
                          />
                        }
                      />
                    </Box>
                  ) : tabFormData ? (
                    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', overflowX: 'hidden', p: 2, boxSizing: 'border-box' }}>
                      <StockBalanceForm
                        formData={tabFormData}
                        canEdit={tab.type === 'new-stock-balance' ? tabPermissions.canCreate : tabPermissions.canUpdate}
                        onChangeForm={(updates) => {
                          const canEditTab = tab.type === 'new-stock-balance' ? tabPermissions.canCreate : tabPermissions.canUpdate;
                          if (!canEditTab) {
                            return;
                          }
                          if (tab.id !== activeTabData?.id) {
                            updateTab(tab.id, {
                              metadata: buildFormMetadata(
                                { ...tabFormData, ...updates },
                                tab.metadata?.stockBalanceId as string | undefined
                              ),
                            });
                            handleUnsavedChangesUpdate(true, tab.id);
                            return;
                          }
                          updateFormData(updates);
                        }}
                        onSave={handleSave}
                        onCancel={() => handleCloseTab(tab.id)}
                      />
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Snackbar open={Boolean(snackbarMessage)} autoHideDuration={3500} onClose={() => setSnackbarMessage(null)}>
        <Alert severity={snackbarMessage?.severity || 'success'} onClose={() => setSnackbarMessage(null)} sx={{ width: '100%' }}>
          {snackbarMessage?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
